import { officeInvoiceIsPaid } from 'components/InvoicePaymentAudit';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import TopBarRedesigned from 'components/TopBarRedesigned/TopBarRedesigned';
import { AdditionalActionsIconType } from 'components/TopBarRedesigned/TopBarRedesigned';
import DentistSideMenu from 'components/DentistSideMenu';
import Api from 'api';
import dayjs from 'dayjs';
import { CalendarMonthOutlined, PersonOutline, SendOutlined, AccessTime } from '@mui/icons-material';
import {
  Content,
  Empty,
  Header,
  ItemAmount,
  ItemHeader,
  ItemMeta,
  ItemTitle,
  List,
  ListItem,
  PageContainer,
  RightColumn,
  SortBar,
  SortButton,
  StatusBadge,
  Subtitle,
  Title,
} from './InvoicesPage.styles';

type InvoiceListItem = {
  id: number;
  hygienist?: { firstName?: string; lastName?: string } | null;
  invoice?: { sentOn?: string; dentistMarkedPaid?: boolean; hygienistMarkedPaid?: boolean; totalInvoiceAmt?: number } | null;
  startDate?: string;
};

const DentistInvoicesPage: React.FC = () => {
  const history = useHistory();
  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');

  const goHome = () => {
    history.push({ pathname: 'dentistHome' });
  };

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        const res = await (Api as any).getInvoices();
        if (!mounted) return;
        setItems(Array.isArray(res) ? res : (res?.data || []));
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load invoices.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const sorted = useMemo(() => {
    const list = [...items];
    if (sortBy === 'date') {
      list.sort((a, b) => {
        const da = a?.invoice?.sentOn ? dayjs(a.invoice!.sentOn).valueOf() : 0;
        const db = b?.invoice?.sentOn ? dayjs(b.invoice!.sentOn).valueOf() : 0;
        return db - da; // desc
      });
    } else {
      list.sort((a, b) => {
        const an = (a?.hygienist?.lastName || '').toLowerCase();
        const bn = (b?.hygienist?.lastName || '').toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return 0;
      });
    }
    return list;
  }, [items, sortBy]);

  const openInvoice = (item: InvoiceListItem) => {
    const payload = { jobId: item.id, fromDentistInvoices: true } as any;
    (window as any).TempStars?.App?.goReact?.('invoice', payload) ?? history.push({ pathname: 'invoice', state: payload });
  };

  // T2-5854: single header row — Back / Title / Hamburger — with the DO side
  // menu rendered inline. Replaces the previous double-header layout.
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

  return (
    <PageContainer>
      <TopBarRedesigned
        onBack={goHome}
        title={'Invoices'}
        additionalActionsIconType={AdditionalActionsIconType.menu}
        onAdditionalActionsButtonClick={() => setIsSideMenuOpen(true)}
      />
      <DentistSideMenu isOpen={isSideMenuOpen} onClose={() => setIsSideMenuOpen(false)} />
      <Content>
        <Header>
          <Title>Invoices</Title>
          <Subtitle>Manage and review all your invoices</Subtitle>
        </Header>

        <SortBar>
          <SortButton active={sortBy === 'date'} onClick={() => setSortBy('date')}>
            <CalendarMonthOutlined fontSize="small" />
            Sort by Date
          </SortButton>
          <SortButton active={sortBy === 'name'} onClick={() => setSortBy('name')}>
            <PersonOutline fontSize="small" />
            Sort by Name
          </SortButton>
        </SortBar>

        {error && <div style={{ color: 'var(--primary-main, #FB7672)', padding: '1rem', textAlign: 'center', background: '#fff', borderRadius: '12px', border: '1px solid var(--primary-main, #FB7672)' }}>{error}</div>}
        {isLoading && <div style={{ padding: '2rem', textAlign: 'center' }}>Loading invoices…</div>}

        {!isLoading && !error && sorted.length === 0 && (
          <Empty>
            <div className="icon">📄</div>
            <div className="title">No Invoices Yet</div>
            <div className="subtitle">Invoices will appear here once shifts are completed</div>
          </Empty>
        )}

        {/* T2-6048: New row layout — name on the left, status pill + amount
            stacked on the right; meta row below with paper-plane "Sent" and
            clock "Shift" date icons. */}
        <List>
          {sorted.map((it) => {
            const hygienistName = it?.hygienist
              ? `${it.hygienist.firstName || ''} ${it.hygienist.lastName || ''}`.trim()
              : 'Unknown Hygienist';
            const isPaid = officeInvoiceIsPaid(it?.invoice);
            const amount = it?.invoice?.totalInvoiceAmt;
            const sentDate = it?.invoice?.sentOn ? dayjs(it.invoice.sentOn).format('MMM DD, YYYY') : '—';
            const shiftDate = it?.startDate ? dayjs(it.startDate).format('MMM DD, YYYY') : null;

            return (
              <ListItem key={it.id} onClick={() => openInvoice(it)}>
                <ItemHeader>
                  <ItemTitle>{hygienistName}</ItemTitle>
                  <ItemMeta>
                    <span>
                      <SendOutlined fontSize="small" />
                      Sent: {sentDate}
                    </span>
                    {shiftDate && (
                      <span>
                        <AccessTime fontSize="small" />
                        Shift: {shiftDate}
                      </span>
                    )}
                  </ItemMeta>
                </ItemHeader>
                <RightColumn>
                  <StatusBadge paid={isPaid}>{isPaid ? 'PAID' : 'UNPAID'}</StatusBadge>
                  {typeof amount === 'number' && (
                    <ItemAmount>${amount.toFixed(2)}</ItemAmount>
                  )}
                </RightColumn>
              </ListItem>
            );
          })}
        </List>
      </Content>
    </PageContainer>
  );
};

export default DentistInvoicesPage;
